import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Route, Router } from '@angular/router';
import { APP_ROUTES_CONFIG, ModuleType, NavItem, Role } from '@contracts';
import { ModuleStore, normalizeRole, SessionStore } from '@core';
import { filter } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  private router = inject(Router);
  private moduleStore = inject(ModuleStore);
  private sessionStore = inject(SessionStore);
  private config = inject(APP_ROUTES_CONFIG);
  private breakpointObserver = inject(BreakpointObserver);
  private APP_ROUTE_CONFIG_PROVIDER = this.config.moduleRoutes;

  siteName = this.config.siteName;
  logoPath = this.config.logoPath;
  version = this.config.version;
  package = this.config.package ?? 'Standard';

  private role = toSignal(this.sessionStore.getRole$(), {
    initialValue: this.sessionStore.getRole(),
  });

  openedSub: Record<string, boolean> = {};
  isMini = true;
  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay(),
  );

  menuItems = computed<NavItem[]>(() => {
    const currentModule = this.moduleStore.module();
    if (!currentModule) return [];
    const routes = this.APP_ROUTE_CONFIG_PROVIDER[currentModule] || [];
    const role = this.role();
    return this.buildMenu(routes, '/', role, currentModule);
  });

  get currentUrl() {
    return this.router.url;
  }

  constructor() {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: any) => this.syncOpenedSub(e.urlAfterRedirects || e.url));
  }

  private buildMenu(
    routes: Route[],
    base: string,
    role: Role,
    module: ModuleType,
  ): NavItem[] {
    const items: NavItem[] = [];
    for (const r of routes) {
      if (r.path === '**' || !r.data?.['nav']) continue;

      const fullPath = this.joinPath(base, r.path || '');
      if (!this.canAccess(r.data?.['access'], role, module)) continue;

      const children = r.children
        ? this.buildMenu(r.children, fullPath, role, module)
        : [];

      items.push({
        ...r.data?.['nav'],
        route: fullPath,
        access: r.data?.['access'],
        sub: children.length ? children : undefined,
      });
    }
    return items.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  }

  toggleSub(item: NavItem, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.openedSub[item.route] = !this.openedSub[item.route];
  }

  isSelected(item: NavItem): boolean {
    const active = this.currentUrl.split('?')[0];
    return (
      active === item.route ||
      (item.sub?.some((s) => active === s.route) ?? false)
    );
  }

  private syncOpenedSub(url: string) {
    this.menuItems().forEach((item) => {
      if (item.sub?.some((s) => url.startsWith(s.route))) {
        this.openedSub[item.route] = true;
      }
    });
  }

  private canAccess(access: any, role: Role, module: ModuleType): boolean {
    if (!access) return true;

    if (access.modules?.length && !access.modules.includes(module)) {
      return false;
    }

    if (
      access.roles?.length &&
      !access.roles.some(
        (allowedRole: string) =>
          normalizeRole(allowedRole) === normalizeRole(role),
      )
    ) {
      return false;
    }

    return true;
  }

  private joinPath(base: string, path: string) {
    return `${base}/${path}`.replace(/\/+/g, '/');
  }

  toggleMini() {
    this.isMini = !this.isMini;
    if (this.isMini) {
      this.openedSub = {};
    }
  }

  handleMenuClick(item: NavItem, drawer: any, event: MouseEvent) {
    if (this.isMini && item.sub) {
      this.isMini = false;
      this.openedSub[item.route] = true;
      return;
    }

    if (item.sub) {
      event.preventDefault();
      event.stopPropagation();
      this.openedSub[item.route] = !this.openedSub[item.route];
    } else {
      this.isMini = true;
      this.openedSub = {};
      this.handleSubMenuClick(drawer);
    }
  }

  closeDrawerIfMobile(drawer: any) {
    const isHandset = this.breakpointObserver.isMatched(Breakpoints.Handset);
    if (isHandset) {
      drawer.close();
    }
  }

  handleSubMenuClick(drawer: any) {
    this.isMini = true;
    this.openedSub = {};

    if (this.breakpointObserver.isMatched(Breakpoints.Handset)) {
      drawer.close();
    }
  }

  getIcon(item: any) {
    return this.isSelected(item) ? item.mini_icon : item.icon;
  }
}
